/**
 * Internal WASM confidence enhancement logic
 * This was previously in the user's lib/agentshield-simple-wasm.ts
 */

/**
 * Check if WASM is available in the current environment
 */
export async function checkWasmAvailability(): Promise<boolean> {
  try {
    // Check if WebAssembly is available
    if (typeof WebAssembly === 'undefined') {
      return false;
    }

    // In Edge Runtime, we can't use WebAssembly.compile()
    // Instead, just check for the presence of WebAssembly object
    // The actual WASM module will be loaded separately
    
    // Check for basic WebAssembly API availability
    if (!WebAssembly.instantiate || !WebAssembly.Module) {
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}

/**
 * Determine if WASM verification should be indicated
 * Based on the detection confidence level
 */
export function shouldIndicateWasmVerification(confidence: number): boolean {
  // WASM verification is indicated for pattern-based detections
  // that achieve high confidence (>= 85 on 0-100 scale)
  return confidence >= 85 && confidence < 100; // Updated to 0-100 scale
}

/**
 * Apply WASM confidence boost for enhanced detection
 */
export function getWasmConfidenceBoost(
  baseConfidence: number,
  reasons: string[] = []
): number {
  // If signature verification succeeded, always return 100%
  if (reasons.some(r =>
    r.includes('signature_agent') &&
    !r.includes('signature_headers_present')
  )) {
    return 100; // Updated to 0-100 scale (was 1.0)
  }

  // For high-confidence pattern detection, boost to 95%
  if (baseConfidence >= 85) { // Updated to 0-100 scale (was 0.85)
    return 95; // Updated to 0-100 scale (was 0.95)
  }

  // For medium-high confidence, apply smaller boost
  if (baseConfidence >= 70) { // Updated to 0-100 scale (was 0.7)
    return Math.min(baseConfidence * 1.1, 90); // Updated to 0-100 scale (was 0.9)
  }

  // No boost for lower confidence
  return baseConfidence;
}

/**
 * Get verification method based on detection
 */
export function getVerificationMethod(
  confidence: number,
  reasons: string[] = []
): string {
  // Check for signature verification
  if (reasons.some(r => 
    r.includes('signature_agent') && 
    !r.includes('signature_headers_present')
  )) {
    return 'signature';
  }
  
  // Check for WASM enhancement
  if (shouldIndicateWasmVerification(confidence)) {
    return 'wasm-enhanced';
  }
  
  // Default to pattern matching
  return 'pattern';
}